"use client";

import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { getEmbedUrl, getThumbnailUrl, formatDuration } from '@/lib/youtube';
import Image from 'next/image';
import { useMediaPlayer } from '@/contexts/media-player-context';

export interface HeroVideoPlayerHandle {
  toggleMute: () => void;
  isMuted: () => boolean;
}

/** Keep artwork visible until the embedded player has an actual video frame. */
export function useYouTubePlaybackReady(iframeRef: React.RefObject<HTMLIFrameElement | null>, videoId: string) {
  const [readyId, setReadyId] = useState<string | null>(null);
  useEffect(() => {
    if (!videoId) return;
    const listen = () => iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening' }), 'https://www.youtube.com');
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.youtube.com' || event.source !== iframeRef.current?.contentWindow) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.event === 'infoDelivery' && [1, 2].includes(data.info?.playerState)) setReadyId(videoId);
      } catch { /* Ignore unrelated or malformed player messages. */ }
    };
    window.addEventListener('message', onMessage);
    const timer = setInterval(listen, 500);
    return () => { window.removeEventListener('message', onMessage); clearInterval(timer); };
  }, [iframeRef, videoId]);
  return readyId === videoId;
}

interface VideoPlayerProps {
  videoId: string;
  title?: string;
  artist?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  className?: string;
  aspectRatio?: '16:9' | '4:3' | '1:1';
  showInfo?: boolean;
}

/**
 * Drive the external VU simulation from the YouTube iframe's REAL state
 * (Gordon: "vu meters can't be moving if no track or video is playing").
 * The iframe streams infoDelivery {playerState, muted} once we send the
 * 'listening' handshake — VU only runs while state===1 (playing) AND unmuted.
 */
function useYTVisualizerSync(iframeRef: React.RefObject<HTMLIFrameElement | null>) {
  const { activateExternalVisualizer, deactivateExternalVisualizer } = useMediaPlayer();
  const activeRef = useRef(false);
  useEffect(() => {
    const st = { state: -1, muted: false };
    const apply = () => {
      const should = st.state === 1 && !st.muted;
      if (should && !activeRef.current) { activeRef.current = true; activateExternalVisualizer(); }
      else if (!should && activeRef.current) { activeRef.current = false; deactivateExternalVisualizer(); }
    };
    const onMsg = (e: MessageEvent) => {
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return;
      let d: any;
      try { d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; } catch { return; }
      if (d?.event !== 'infoDelivery' || !d.info) return;
      if (typeof d.info.playerState === 'number') st.state = d.info.playerState;
      if (typeof d.info.muted === 'boolean') st.muted = d.info.muted;
      apply();
    };
    window.addEventListener('message', onMsg);
    // Repeating handshake — survives iframe re-creation (mute-toggle key swaps)
    const hs = setInterval(() => {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 'bb-vu' }), '*');
    }, 1500);
    return () => {
      window.removeEventListener('message', onMsg);
      clearInterval(hs);
      if (activeRef.current) { activeRef.current = false; deactivateExternalVisualizer(); }
    };
  }, [iframeRef, activateExternalVisualizer, deactivateExternalVisualizer]);
}

export default function VideoPlayer({
  videoId,
  title,
  artist,
  autoplay = false,
  muted = false,
  loop = false,
  controls = true,
  className = '',
  aspectRatio = '16:9',
  showInfo = true,
}: VideoPlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useYTVisualizerSync(iframeRef);

  const embedUrl = getEmbedUrl(videoId, {
    autoplay,
    mute: muted,
    loop,
    controls,
    modestBranding: true,
  });

  const aspectClasses = {
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '1:1': 'aspect-square',
  };

  return (
    <div className={`w-full ${className}`}>
      <div className={`relative w-full ${aspectClasses[aspectRatio]} bg-black rounded-lg overflow-hidden`}>
        <iframe
          ref={iframeRef}
          data-bb-muted={muted ? 'true' : 'false'}
          className="absolute inset-0 w-full h-full"
          src={embedUrl}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={title || 'Video player'}
        />
      </div>
      {showInfo && (title || artist) && (
        <div className="mt-2 px-1">
          {title && (
            <h3 className="text-sm font-semibold text-foreground truncate">{title}</h3>
          )}
          {artist && (
            <p className="text-xs text-muted-foreground truncate">{artist}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Hero video variant - full-width background embed for the hero section
 */
export const HeroVideoPlayer = forwardRef<HeroVideoPlayerHandle, { videoId: string; className?: string }>(
  function HeroVideoPlayer({ videoId, className = '' }, ref) {
    const [muted, setMuted] = useState(true);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const ready = useYouTubePlaybackReady(iframeRef, videoId);
    // VU follows the iframe's real playing+unmuted state — a muted or paused
    // hero video must leave the meters completely still.
    useYTVisualizerSync(iframeRef);

    useImperativeHandle(ref, () => ({
      toggleMute: () => {
        const newMuted = !muted;
        setMuted(newMuted);
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: newMuted ? 'mute' : 'unMute', args: '' }),
            '*'
          );
        }
      },
      isMuted: () => muted,
    }), [muted]);

    const embedUrl = getEmbedUrl(videoId, {
      autoplay: true,
      mute: muted,
      loop: true,
      controls: false,
      modestBranding: true,
    });

    return (
      <>
      <Image src={getThumbnailUrl(videoId)} alt="" fill sizes="100vw" priority className="object-cover" />
      <iframe
        ref={iframeRef}
        data-bb-muted={muted ? 'true' : 'false'}
        key={videoId + (muted ? '-m' : '-u')}
        className={`absolute top-1/2 left-1/2 border-0 transition-opacity ${ready ? 'opacity-100' : 'opacity-0'} ${className}`}
        // No pointer + drawn 15% larger: YouTube never shows its hover UI,
        // middle play/pause buttons or title strip over the hero (Gordon 2026-10-30).
        style={{ width: 'max(177.78vh, 100vw)', height: 'max(56.25vw, 100vh)', minWidth: '100%', minHeight: '100%', transform: 'translate(-50%, -50%) scale(1.15)', pointerEvents: 'none' }}
        src={embedUrl}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Hero video"
      />
      </>
    );
  }
);
